import type { Ref } from 'vue';
import type { MessageApi } from 'naive-ui';
import type { ChamferFeatureDefinition, FeatureDefinition, FilletFeatureDefinition } from '@puppycad/cad-core';
import type { ExtrudeViewportRef } from '../extrude-types';
import { buildChamferDraft, buildFilletDraft, normalizeErrorMessage, type ActiveBlendKind } from './blend-feature-helpers';
import { syncCommittedBlendFeatureResult } from './blend-commit-result';

interface BlendSubmitterContext {
  activeKind: Ref<ActiveBlendKind>;
  editingFeatureId: Ref<string | null>;
  upstreamBodyObjectId: Ref<string | null>;
  filletDefinition: Ref<FilletFeatureDefinition>;
  chamferDefinition: Ref<ChamferFeatureDefinition>;
  filletCanSubmit: Ref<boolean>;
  chamferCanSubmit: Ref<boolean>;
  filletPreviewError: Ref<string | null>;
  chamferPreviewError: Ref<string | null>;
  viewportRef: ExtrudeViewportRef;
  updateState: () => Promise<void>;
  message: MessageApi;
  freezePreviewForCommit: () => void;
  restoreViewportSelectionEnvironment: () => void;
  clearPreviewAsync: () => Promise<void>;
  resetSession: (restoreFeature: boolean) => void;
  setCommitTransitionActive: (active: boolean) => void;
}

/**
 * 创建圆角/倒角提交流程。
 *
 * @param context 提交流程上下文。
 * @returns 提交流程函数。
 */
export function createBlendSubmitter(context: BlendSubmitterContext) {
  const {
    activeKind,
    editingFeatureId,
    upstreamBodyObjectId,
    filletDefinition,
    chamferDefinition,
    filletCanSubmit,
    chamferCanSubmit,
    filletPreviewError,
    chamferPreviewError,
    viewportRef,
    updateState,
    message,
    freezePreviewForCommit,
    restoreViewportSelectionEnvironment,
    clearPreviewAsync,
    resetSession,
    setCommitTransitionActive,
  } = context;

  const submit = async (kind: 'fillet' | 'chamfer'): Promise<void> => {
    const canSubmit = kind === 'fillet' ? filletCanSubmit.value : chamferCanSubmit.value;
    const previewError = kind === 'fillet' ? filletPreviewError.value : chamferPreviewError.value;
    if (activeKind.value !== kind || !canSubmit) {
      if (previewError) {
        message.warning(previewError);
      }
      return;
    }

    if (previewError) {
      message.warning(previewError);
      return;
    }

    try {
      setCommitTransitionActive(true);
      freezePreviewForCommit();
      const draft = kind === 'fillet'
        ? buildFilletDraft({
          definition: filletDefinition.value,
          upstreamBodyObjectId: upstreamBodyObjectId.value,
          editingFeatureId: editingFeatureId.value,
        })
        : buildChamferDraft({
          definition: chamferDefinition.value,
          upstreamBodyObjectId: upstreamBodyObjectId.value,
          editingFeatureId: editingFeatureId.value,
        });

      const result = await viewportRef.value?.commitFeature?.({
        featureKind: kind,
        featureId: editingFeatureId.value,
        definition: draft.definitionModel as unknown as FeatureDefinition,
        bindings: draft.bindings,
      });
      if (!result) {
        throw new Error(kind === 'fillet' ? '圆角特征提交失败。' : '倒角特征提交失败。');
      }

      restoreViewportSelectionEnvironment();
      await syncCommittedBlendFeatureResult({
        viewportRef,
        upstreamBodyObjectId,
        result,
      });
      await clearPreviewAsync();
      await updateState();
      resetSession(false);
    } catch (error) {
      message.error(normalizeErrorMessage(error, kind === 'fillet' ? '圆角特征提交失败。' : '倒角特征提交失败。'));
    } finally {
      setCommitTransitionActive(false);
    }
  };


  return submit;
}
